import { ORDER } from '../core/systems.js';
import { ORDERS } from '../ai/store.js';
import { TEAM } from '../core/entities.js';

/**
 * ===========================================================================
 *  TACTICS — the first input that reaches the army
 * ===========================================================================
 *
 * DEV_PLAN Step 5, and the gap analysis calls it the highest
 * felt-change-per-unit-work item in the document for one reason: the order
 * layer was ALREADY THERE. `src/ai/command.js` and `src/ai/squad.js` run a
 * full re-tasking pass every tick and obey HOLD / ADVANCE / ESCORT / ENGAGE /
 * ROUT. What was missing was any way for the player to say a word to it.
 *
 * WHY FOUR VERBS AND NOT A WHEEL. A wheel is a menu, and a menu in a musou is
 * a pause: the moment the player has to read, the battle they were ordering
 * about has moved. Four digits are four intents, each one keypress, issued
 * without leaving the fight. The plan allowed either.
 *
 * THE ORDERS ARE LOCAL, NOT ARMY-WIDE. Only allied squads inside `RADIUS` of
 * the player take the order, and at most `MAX_SQUADS` of them. An army-wide
 * button would be a strategy game's verb — one press, whole map, no position
 * required. Making it local means standing in the right place IS the cost, and
 * that is the decision the row is supposed to measure.
 *
 * A player order outranks the command layer for `HOLD_SECONDS` — see
 * `squads.playerT`, which command.js checks before re-tasking. Without that
 * hold the order would be overwritten within a second and the whole feature
 * would be indistinguishable from not being wired up.
 */

/** How far an order carries, in metres. */
export const RADIUS = 80;
/** How many squads one order can reach. */
export const MAX_SQUADS = 3;
/** How long the player's word outranks `command.js`. */
export const HOLD_SECONDS = 12;
/** A floor between orders, so a held key is one order and not sixty. */
export const COOLDOWN = 0.45;

/**
 * The four verbs.
 *
 * Each maps onto an order the AI already honours, so none of this invents
 * behaviour — it names intents the army could always have been given.
 */
export const VERBS = [
  {
    key: 'order1', id: 'hold', label: 'Hold the Line',
    shout: 'Hold! Give no ground!',
    plan: (t) => ({ order: ORDERS.HOLD }),
  },
  {
    key: 'order2', id: 'press', label: 'Press the Attack',
    shout: 'Press them! Break their line!',
    // ADVANCE at the nearest enemy squad. The AI's own `march` shape.
    plan: (t) => {
      const foe = t.nearestFoeSquad();
      return foe >= 0
        ? { order: ORDERS.ADVANCE, gx: t.S.ax[foe], gz: t.S.az[foe], objSq: foe }
        : null;
    },
  },
  {
    key: 'order3', id: 'onme', label: 'On Me',
    shout: 'To me! Form on the banner!',
    // ESCORT takes an ENTITY id, and the player is one.
    plan: (t) => ({ order: ORDERS.ESCORT, escort: t.w.playerId,
      gx: t.px(), gz: t.pz() }),
  },
  {
    key: 'order4', id: 'take', label: 'Take the Position',
    shout: 'Take the ground! Raise our banner!',
    plan: (t) => {
      const s = t.nearestContestedSite();
      return s ? { order: ORDERS.ADVANCE, gx: s.x, gz: s.z } : null;
    },
  },
];

const CSS = `
/* LEFT COLUMN, ABOVE THE PROGRESSION STRIP.
   Two placements were wrong before this one and BOTH were invisible in the CSS.
   right:30u bottom:150u drew straight over the minimap, which is anchored at
   right:28u bottom:26u and is tall enough to reach through 150u
   (shots/tacui/000_order_given.png). Moving to the left column at 156u then
   landed on the progression strip, whose three rows run to about 175u and not
   the 146u I had estimated — the two blocks ended up two pixels apart
   (shots/tacui2/001_order_after.png).
   The left column is the player's own and now stacks cleanly: plate 26-104u,
   progression strip 116-175u, this above both at 186u. Measured off frames,
   not off arithmetic. */
.sb .tac{position:absolute; left:calc(var(--u)*30); bottom:calc(var(--u)*186);
  width:calc(var(--u)*306); opacity:0}
.sb .tac .trow{display:flex; align-items:baseline; gap:calc(var(--u)*8)}
.sb .tac .tk{font-size:calc(var(--u)*8.5); font-weight:700; letter-spacing:calc(var(--u)*3.2);
  text-transform:uppercase; color:rgba(226,190,124,.62)}
.sb .tac .tn{font-size:calc(var(--u)*15); letter-spacing:calc(var(--u)*1.6);
  color:#fbf1dc; line-height:1;
  text-shadow:0 calc(var(--u)*2) calc(var(--u)*8) rgba(0,0,0,.9)}
.sb .tac .tsub{margin-top:calc(var(--u)*4); font-size:calc(var(--u)*9.5);
  letter-spacing:calc(var(--u)*1.6); color:rgba(232,196,140,.74)}
`;

function tx(el, s) { if (el.__t !== s) { el.__t = s; el.textContent = s; } }
function op(el, v) {
  v = v < 0 ? 0 : v > 1 ? 1 : v;
  if (el.__o !== undefined && Math.abs(el.__o - v) < 0.004) return;
  el.__o = v; el.style.opacity = v.toFixed(3);
}

export function createTactics() {
  let W = null, cd = 0, mounted = false;
  let root = null, styleEl = null;
  const R = {};
  const S = { show: 0 };
  /** Counters a probe reads. Orders given, and squads actually reached. */
  /* `touched` is every DISTINCT squad this battle's orders have reached. A
   * probe comparing an ordered run against a control needs it: only three
   * squads take any one order, so averaging a position change over every squad
   * in earshot buries the effect under a crowd that was never spoken to. */
  const stats = { orders: 0, squadsOrdered: 0, refused: 0, byVerb: {}, lastT: -1,
    touched: [], lastSquads: [] };
  let lastLabel = '', lastN = 0, lastShownT = -1;

  const ctx = {
    get w() { return W; },
    get S() { return W.ai.squads; },
    px: () => W.entities.x[W.playerId],
    pz: () => W.entities.z[W.playerId],

    /** The nearest ENEMY squad to the player, or -1. */
    nearestFoeSquad() {
      const S2 = W.ai.squads, e = W.entities;
      const x = e.x[W.playerId], z = e.z[W.playerId];
      let best = -1, bd = 1e18;
      for (let j = 0; j < S2.cap; j++) {
        if (!S2.alive[j] || S2.count[j] <= 0) continue;
        if (S2.team[j] !== TEAM.ENEMY) continue;
        const dx = S2.ax[j] - x, dz = S2.az[j] - z;
        const d = dx * dx + dz * dz;
        if (d < bd) { bd = d; best = j; }
      }
      return best;
    },

    /**
     * The nearest base this side does not already hold, or null.
     *
     * `world.bases.list` and not `world.mission.bases`: the mission's own view
     * of a base is a capture RECORD (id, team, progress) with no coordinates on
     * it, and an order needs somewhere to march to.
     */
    nearestContestedSite() {
      const list = W.bases && W.bases.list;
      if (!list || !list.length) return null;
      const e = W.entities;
      const x = e.x[W.playerId], z = e.z[W.playerId];
      let best = null, bd = 1e18;
      for (let i = 0; i < list.length; i++) {
        const b = list[i];
        if (b.x === undefined || b.z === undefined) continue;
        if ((b.team | 0) === TEAM.PLAYER) continue;      // already ours
        const dx = b.x - x, dz = b.z - z;
        const d = dx * dx + dz * dz;
        if (d < bd) { bd = d; best = { x: b.x, z: b.z, name: b.name || 'the position' }; }
      }
      return best;
    },
  };

  /** The allied squads within reach, nearest first, capped. */
  function reachable(out) {
    const S2 = W.ai.squads, e = W.entities;
    const x = e.x[W.playerId], z = e.z[W.playerId];
    const R2 = RADIUS * RADIUS;
    out.length = 0;
    for (let j = 0; j < S2.cap; j++) {
      if (!S2.alive[j] || S2.count[j] <= 0) continue;
      if (S2.team[j] !== TEAM.PLAYER) continue;
      // a routing squad is not listening to anyone
      if (S2.order[j] === ORDERS.ROUT) continue;
      const dx = S2.ax[j] - x, dz = S2.az[j] - z;
      const d = dx * dx + dz * dz;
      if (d > R2) continue;
      out.push(j, d);
    }
    return out;
  }

  const _pairs = [];
  const _pick = [];

  /** Issue `verb`. Returns the number of squads that took it. */
  function issue(verb) {
    if (!W || !W.ai || !W.ai.squads) return 0;
    const plan = verb.plan(ctx);
    if (!plan) { stats.refused++; return 0; }

    reachable(_pairs);
    // nearest first — a hand-rolled selection sort over pairs, because this
    // runs on a keypress and allocating a sorted array of objects for four
    // squads would be worse than the loop
    _pick.length = 0;
    const n = _pairs.length / 2;
    for (let k = 0; k < MAX_SQUADS && k < n; k++) {
      let bi = -1, bd = 1e18;
      for (let a = 0; a < _pairs.length; a += 2) {
        if (_pairs[a] < 0) continue;
        if (_pairs[a + 1] < bd) { bd = _pairs[a + 1]; bi = a; }
      }
      if (bi < 0) break;
      _pick.push(_pairs[bi]);
      _pairs[bi] = -1;
    }
    if (!_pick.length) { stats.refused++; return 0; }

    const S2 = W.ai.squads;
    for (let k = 0; k < _pick.length; k++) {
      const i = _pick[k];
      if (plan.escort !== undefined) S2.escort[i] = plan.escort;
      if (plan.objSq !== undefined) S2.objSq[i] = plan.objSq;
      const gx = plan.gx !== undefined ? plan.gx : S2.ax[i];
      const gz = plan.gz !== undefined ? plan.gz : S2.az[i];
      S2.setOrder(i, plan.order, gx, gz);
      // the hold that makes the order stick past command.js's next pass
      S2.playerT[i] = HOLD_SECONDS;
    }

    stats.orders++;
    stats.squadsOrdered += _pick.length;
    stats.lastSquads = _pick.slice();
    for (let k = 0; k < _pick.length; k++) {
      if (stats.touched.indexOf(_pick[k]) < 0) stats.touched.push(_pick[k]);
    }
    stats.byVerb[verb.id] = (stats.byVerb[verb.id] || 0) + 1;
    stats.lastT = W.time ? W.time.sim : -1;

    /* Feedback, three ways, because an order the player cannot hear or see is
     * an order they will not believe was given: a called shout, a banner line,
     * and the squads themselves turning (which is the order layer's own doing
     * and needs nothing from here). */
    const e = W.entities, id = W.playerId;
    W.audio?.play?.('voice_shout', { x: e.x[id], y: e.y[id] + 1.6, z: e.z[id], gain: 0.9 });
    W.ui?.banner?.(verb.label, verb.shout, 1.8);
    W.ui?.toast?.(`${verb.label} — ${_pick.length} squad${_pick.length === 1 ? '' : 's'}`);
    lastLabel = verb.label; lastN = _pick.length; lastShownT = 0;
    return _pick.length;
  }

  function mount(w) {
    if (mounted) return true;
    const host = w.ui && w.ui.root;
    if (!host || !host.appendChild) return false;
    styleEl = document.createElement('style');
    styleEl.textContent = CSS;
    host.appendChild(styleEl);
    root = document.createElement('div');
    root.className = 'tac';
    root.innerHTML = `<div class="trow"><span class="tk">Order</span><span class="tn ser"></span></div>
      <div class="tsub"></div>`;
    host.appendChild(root);
    R.name = root.querySelector('.tn');
    R.sub = root.querySelector('.tsub');
    mounted = true;
    return true;
  }

  return {
    name: 'tactics', order: ORDER.UI,

    init(w) {
      W = w;
      w.tactics = {
        /** Issue by verb id. The probe's entry point, and a debug key's. */
        order(id) {
          const v = VERBS.find((x) => x.id === id || x.key === id);
          return v ? issue(v) : 0;
        },
        verbs: VERBS,
        stats,
        get radius() { return RADIUS; },
        get maxSquads() { return MAX_SQUADS; },
        get holdSeconds() { return HOLD_SECONDS; },
        /** How many allied squads are in earshot right now. */
        inRange() { return reachable(_pairs).length / 2; },
        reset() {
          stats.orders = 0; stats.squadsOrdered = 0; stats.refused = 0;
          stats.byVerb = {}; stats.lastT = -1;
          stats.touched.length = 0; stats.lastSquads.length = 0;
        },
      };
    },

    fixedUpdate(w, dt) {
      if (cd > 0) cd -= dt;
      const inp = w.input;
      if (!inp) return;
      // Orders are a battle verb: not on the front end, not over a modal, and
      // not while the mission is not running.
      const m = w.mission;
      if (!m || m.state !== 'active' || m.paused) return;
      if (w.ui && w.ui.state !== 'playing') return;
      if (cd > 0) return;
      for (let i = 0; i < VERBS.length; i++) {
        if (inp.consume && inp.consume(VERBS[i].key, 0.25)) {
          issue(VERBS[i]);
          cd = COOLDOWN;
          return;
        }
      }
    },

    update(w, dt, alpha, realDt) {
      if (!mounted && !mount(w)) return;
      const k = Math.min(0.1, realDt > 0 ? realDt : 1 / 60);
      if (lastShownT >= 0) lastShownT += k;
      const m = w.mission;
      const live = !!m && m.state === 'active' && !m.paused &&
        (!w.ui || w.ui.state === 'playing');
      // show the last order for a few seconds, then fade
      const want = (live && lastShownT >= 0 && lastShownT < 4.5) ? 1 : 0;
      S.show += (want - S.show) * Math.min(1, k * 7);
      op(root, S.show);
      if (S.show < 0.004) return;
      tx(R.name, lastLabel);
      tx(R.sub, lastN > 0 ? `${lastN} squad${lastN === 1 ? '' : 's'} answered` : 'No one in earshot');
    },

    dispose() {
      root && root.remove(); styleEl && styleEl.remove();
      mounted = false;
    },
  };
}
